import React from 'react';
import { useOSBContentFragment } from '../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import { useViewport } from '../../../../hooks/owners-osb';
import './retrieve-access-code.scss';
import { FMButton, FMInput } from '@own/fds-react';
import ServiceHandler from '../../../../services/service-handler';
import { OsbLoader } from '../../../common/osb-loader/osb-loader';

interface RetrieveAccessCodeContent {
    findBookingLabel?: string;
    findBookingButtonLabel?: string;
    recoverAccessCodeLabel?: string;
    recoverAccessCodeDescription?: string;
    emailInputPlaceHolder?: string;
    recoverAccessCodeButtonLabel?: string;
    invalidEmailTextMessage?: string;
    validEmailTextMessage?: string;
    noEmailTextMessage?: string;
    emailValidationRegex?: string;
}

const RetrieveAccessCode: React.FC = () => {
    const osbContentData = (useOSBContentFragment('common/osb-v3', 'retrieve-access-code') as RetrieveAccessCodeContent) || {};
    const [loading, setLoading] = React.useState<boolean>(false);
    const { isMobileView } = useViewport();
    const [email, setEmail] = React.useState('');

    type RetrieveStatus = 'idle' | 'invalid' | 'sent' | 'not_found';
    const [status, setStatus] = React.useState<RetrieveStatus>('idle');

    const validateEmail = (value: string): boolean => {
        const raw = osbContentData.emailValidationRegex;
        if (!raw) return false;
        let pattern = raw;
        let flags = '';
        if (raw.startsWith('/')) {
            const lastSlash = raw.lastIndexOf('/');
            if (lastSlash > 0) {
                pattern = raw.slice(1, lastSlash);
                flags = raw.slice(lastSlash + 1);
            }
        }
        try {
            const re = new RegExp(pattern, flags);
            return re.test(value.trim());
        } catch {
            return false;
        }
    };

    const handleEmailSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const trimmed = email.trim();
        if (!validateEmail(trimmed)) {
            setStatus('invalid');
            return;
        }
        setLoading(true);
        ServiceHandler.OsbRetrieveAccessCodeService.request(trimmed)
            .then((response) => {
                if (response?.emailNotificationsSent) {
                    setStatus('sent');
                } else {
                    setStatus('not_found');
                }
            })
            .catch((error) => {
                console.error('Error retrieving access code:', error);
            })
            .finally(() => setLoading(false));
    };

    const onEmailChange = (e: any) => {
        setEmail(e.target.value);
        if (status !== 'idle') setStatus('idle');
    };

    // Destructure with defaults (defaults only apply if value is undefined, not for empty string)
    const {
        findBookingLabel = '',
        findBookingButtonLabel= '',
        recoverAccessCodeLabel = '',
        recoverAccessCodeDescription = '',
        emailInputPlaceHolder = '',
        recoverAccessCodeButtonLabel = '',
        invalidEmailTextMessage = '',
        validEmailTextMessage = '',
        noEmailTextMessage = ''
    } = osbContentData || {};

    const renderStatusMessage = () => {
        if (status === 'idle') return null;
        const map: Record<Exclude<RetrieveStatus, 'idle'>, { wrapper: string; textCls: string; text: string }> = {
            invalid: {
                wrapper: 'invalidEmailWrapper',
                textCls: 'invalidEmailText',
                text: invalidEmailTextMessage
            },
            sent: {
                wrapper: 'validEmailWrapper',
                textCls: 'validEmailText',
                text: validEmailTextMessage
            },
            not_found: {
                wrapper: 'noEmailTextWrapper',
                textCls: 'noEmailText',
                text: noEmailTextMessage
            }
        };
        const cfg = map[status as Exclude<RetrieveStatus, 'idle'>];
        return (
            <div className={cfg.wrapper}>
                <span className={cfg.textCls}>{cfg.text}</span>
            </div>
        );
    };

    return (
        <>
            {loading && <OsbLoader />}
            <div className="retreiveAccessCodeOuterWrapper">
                <div className="retrieveAccessCodeWrapper">
                    <div className="retrieveAccessCodeTitle">
                        {recoverAccessCodeLabel}
                    </div>
                    <div className="retrieveAccessCodeDescription">
                        {recoverAccessCodeDescription}
                    </div>
                    {renderStatusMessage()}
                    <div className="retrieveAccessCodeFormWrapper">
                        <form className="retrieveAccessCodeForm" onSubmit={handleEmailSubmit} noValidate>
                            <FMInput
                                type="email"
                                className="emailInput"
                                value={email}
                                placeholder={String(emailInputPlaceHolder) || ''}
                                onChange={onEmailChange}
                                aria-invalid={status === 'invalid'}
                                disabled={loading}
                            />
                            {(loading || recoverAccessCodeButtonLabel) && (
                                <FMButton
                                    type="primary"
                                    label={loading ? '...' : recoverAccessCodeButtonLabel}
                                    onClick={() => handleEmailSubmit()}
                                    disabled={loading}
                                />
                            )}
                        </form>
                    </div>
                </div>
            </div>
            {!isMobileView && (
                <div className="findBookingRedirectionWrapper">
                    <div className="findBookingDescriptionLabel">
                        {findBookingLabel}
                    </div>
                    <div className="findBookingButton">
                        {findBookingButtonLabel && (
                            <FMButton
                                type="primary"
                                label={findBookingButtonLabel}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default RetrieveAccessCode;
